import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  ArrowRight,
  BadgePercent,
  Building2,
  CalendarCheck,
  CalendarClock,
  Camera,
  Check,
  ClipboardCheck,
  Copy,
  CreditCard,
  Mail,
  MapPin,
  Network,
  Package,
  Receipt,
  ShieldCheck,
  ShoppingBag,
  Store,
  User,
  UsersRound,
  type LucideIcon,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { authApi } from '@/services/auth'
import { orgTreeApi } from '@/services/org-tree'
import { buildPostSelectionPath } from '@/lib/auth-redirect'
import { getErrorMessage } from '@/lib/api'
import { cn } from '@/lib/utils'
import { AuthTermsDisclosure } from '@/components/auth/auth-terms-disclosure'
import {
  FieldError,
  FormSection,
  IconInput,
  PasswordChecklist,
  PasswordInput,
  PhoneInput,
  SignupSplitShell,
  type PasswordRule,
  type SignupBrand,
} from '@/components/auth/signup-split-shell'
import { SignupEmailCodeField, useSignupEmailCode } from '@/hooks/use-signup-email-code'

const PHONE_NUMBER_SAMPLE_PLACEHOLDER = '9876543210'

const PASSWORD_RULES: PasswordRule[] = [
  { label: 'At least 8 characters', test: (value) => value.length >= 8 },
  { label: 'An uppercase letter', test: (value) => /[A-Z]/.test(value) },
  { label: 'A lowercase letter', test: (value) => /[a-z]/.test(value) },
  { label: 'At least one number', test: (value) => /\d/.test(value) },
]

type StartCompanyVertical = 'ecommerce' | 'cleaning' | 'home-services'

function WordmarkLogo({ icon: Icon, name, compact = false }: { icon: LucideIcon; name: string; compact?: boolean }) {
  return (
    <div className={cn('inline-flex items-center gap-3', compact ? '' : 'rounded-2xl bg-white px-4 py-3 shadow-lg shadow-slate-950/10')}>
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#4f46e5] to-[#c026d3] text-white">
        <Icon className="h-5 w-5" />
      </span>
      <span className="leading-tight">
        <span className="block text-base font-black tracking-tight text-slate-900">{name}</span>
        <span className="block text-[11px] font-semibold text-slate-500">by Ellantec</span>
      </span>
    </div>
  )
}

const facilityProLogo = (
  <div className="inline-flex items-center rounded-2xl bg-white px-4 py-3 shadow-lg shadow-slate-950/10">
    <img src="/facilitypro-logo.png" alt="FacilityPro" className="h-9 w-auto" />
  </div>
)
const facilityProMobileLogo = <img src="/facilitypro-logo.png" alt="FacilityPro" className="h-8 w-auto" />
const facilityProGradient = 'from-[#0b3a63] via-[#13598a] to-[#1c6849]'
const facilityProTrustPoints = [
  { icon: Check, label: 'All features included' },
  { icon: Check, label: 'No card required' },
  { icon: ShieldCheck, label: 'Role-based access' },
]

const VERTICAL_CONFIG: Record<StartCompanyVertical, {
  title: string
  description: string
  businessLabel: string
  namePlaceholder: string
  submitLabel: string
  creatingLabel: string
  brand: SignupBrand
}> = {
  ecommerce: {
    title: 'Start your online store',
    description: "Create your E-Commerce account in one step. You'll be signed in straight away.",
    businessLabel: 'Store name',
    namePlaceholder: 'Acme Store',
    submitLabel: 'Create store',
    creatingLabel: 'Creating your store…',
    brand: {
      logo: <WordmarkLogo icon={ShoppingBag} name="E-Commerce" />,
      mobileLogo: <WordmarkLogo icon={ShoppingBag} name="E-Commerce" compact />,
      eyebrow: 'Sell online and in store',
      headline: 'Launch your store and start selling.',
      description: 'Products, orders and payments in one place — your storefront is ready the moment you sign in.',
      highlights: [
        { icon: Package, title: 'Products & catalogue', text: 'Categories, variants and stock for everything you sell.' },
        { icon: CreditCard, title: 'Orders & payments', text: 'Track every order from checkout to delivery.' },
        { icon: BadgePercent, title: 'Offers', text: 'Run discounts and promotions without extra tools.' },
        { icon: Store, title: 'Point of sale', text: 'Sell in person from the same catalogue and stock.' },
      ],
      trustPoints: [
        { icon: Check, label: 'Set up in minutes' },
        { icon: Check, label: 'No card required' },
        { icon: ShieldCheck, label: 'Role-based access' },
      ],
      gradientClassName: 'from-[#4338ca] via-[#7c3aed] to-[#c026d3]',
    },
  },
  cleaning: {
    title: 'Start your cleaning company',
    description: "Create your FacilityPro cleaning account in one step. You'll be signed in straight away.",
    businessLabel: 'Company name',
    namePlaceholder: 'Acme Cleaning',
    submitLabel: 'Create cleaning company',
    creatingLabel: 'Creating your company…',
    brand: {
      logo: facilityProLogo,
      mobileLogo: facilityProMobileLogo,
      eyebrow: 'Cleaning operations, organised',
      headline: 'Run every site and shift with confidence.',
      description: 'Schedules, teams and proof of work in one place — ready the moment you sign in.',
      highlights: [
        { icon: CalendarClock, title: 'Recurring schedules', text: 'Plan regular site visits and shifts in minutes.' },
        { icon: MapPin, title: 'Attendance & location', text: 'See who is on site and when they clocked in.' },
        { icon: ClipboardCheck, title: 'Checklists & evidence', text: 'Capture photos and sign-offs for every job.' },
        { icon: Network, title: 'Multi-branch', text: 'Run several branches with clear oversight.' },
      ],
      trustPoints: facilityProTrustPoints,
      gradientClassName: facilityProGradient,
    },
  },
  'home-services': {
    title: 'Start your home services company',
    description: "Create your FacilityPro home services account in one step. You'll be signed in straight away.",
    businessLabel: 'Company name',
    namePlaceholder: 'Acme Home Services',
    submitLabel: 'Create home services company',
    creatingLabel: 'Creating your company…',
    brand: {
      logo: facilityProLogo,
      mobileLogo: facilityProMobileLogo,
      eyebrow: 'Home services, simplified',
      headline: 'Book, assign and complete jobs without the chaos.',
      description: 'Bookings, technicians and invoices in one place — ready the moment you sign in.',
      highlights: [
        { icon: CalendarCheck, title: 'Bookings', text: 'Take and schedule customer jobs in one calendar.' },
        { icon: UsersRound, title: 'Team assignment', text: 'Send each job to the right technician.' },
        { icon: Camera, title: 'Proof of work', text: 'Photos and notes from every visit.' },
        { icon: Receipt, title: 'Invoicing', text: 'Bill customers as soon as the job is done.' },
      ],
      trustPoints: facilityProTrustPoints,
      gradientClassName: facilityProGradient,
    },
  },
}

const startCompanySignupSchema = z
  .object({
    company_name: z.string().trim().min(1, 'Name is required'),
    owner_name: z.string().trim().min(1, 'Your name is required'),
    email: z.string().email('Please enter a valid email address'),
    phone_number: z.string().trim().min(1, 'Phone number is required'),
    country_id: z.string().trim().min(1, 'Country is required'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number'),
    password_confirm: z.string(),
  })
  .refine((data) => data.password === data.password_confirm, {
    message: 'Passwords do not match',
    path: ['password_confirm'],
  })

type StartCompanySignupFormData = z.infer<typeof startCompanySignupSchema>

export default function SignupStartCompanyPage() {
  const { vertical } = useParams<{ vertical: string }>()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const emailCode = useSignupEmailCode()
  const [businessCode, setBusinessCode] = useState<string | null>(null)
  const [pendingRedirectPath, setPendingRedirectPath] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const config = vertical && vertical in VERTICAL_CONFIG ? VERTICAL_CONFIG[vertical as StartCompanyVertical] : null

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<StartCompanySignupFormData>({
    resolver: zodResolver(startCompanySignupSchema),
    defaultValues: {
      country_id: '94',
    },
  })

  const selectedCountryId = watch('country_id')
  const password = watch('password') ?? ''

  const countriesQuery = useQuery({
    queryKey: ['org-tree-countries'],
    queryFn: orgTreeApi.listCountries,
    staleTime: 60_000,
    enabled: Boolean(config),
  })

  const selectedCountry = countriesQuery.data?.find((country) => String(country.id) === selectedCountryId)

  useEffect(() => {
    if (selectedCountryId || !countriesQuery.data?.length) {
      return
    }

    setValue('country_id', String(countriesQuery.data[0].id), { shouldValidate: false, shouldDirty: false })
  }, [countriesQuery.data, selectedCountryId, setValue])

  if (vertical === 'community') {
    return <Navigate to="/signup/community-company" replace />
  }

  if (!config) {
    return <Navigate to="/signup/company" replace />
  }

  const watchedEmail = watch('email') ?? ''

  const onSubmit = async (data: StartCompanySignupFormData) => {
    // The first submit emails a verification code; the next one sends it with the signup.
    const verificationCode = await emailCode.prepare(data.email)
    if (verificationCode === null) return
    setIsLoading(true)
    try {
      // No `identifier` here -- the backend generates the Business Code
      // (e.g. ABCDEF), which is what every other admin/worker will need
      // to join this same org afterward.
      const response = await authApi.signup({
        ...(verificationCode ? { email_code: verificationCode } : {}),
        signup_type: 'company',
        company_name: data.company_name,
        facility_product: vertical,
        owner_name: data.owner_name,
        email: data.email,
        phone_number: data.phone_number,
        country_id: Number(data.country_id),
        password: data.password,
      })

      // company_identifier is the human-readable Business Code login actually
      // accepts as `tenant`; service_instance_id is an internal system ID
      // (e.g. "102-923226901378") that looks similar but isn't a valid login
      // reference -- only fall back to it if the Business Code is somehow
      // missing from the response.
      const loginTenant = response.data.company_identifier || response.data.service_instance_id

      if (loginTenant) {
        try {
          const loginResponse = await authApi.login({
            tenant: loginTenant,
            email: data.email,
            password: data.password,
          })

          if (loginResponse.data?.access_token) {
            setPendingRedirectPath(buildPostSelectionPath(loginResponse.data.service_instance_id || loginTenant))
            setBusinessCode(response.data.company_identifier || loginTenant)
            return
          }
        } catch {
          // Signup succeeded; fall through to the sign-in redirect.
        }
      }

      toast.success('Account created successfully! Sign in to continue.')
      navigate(loginTenant ? `/login?tenant=${encodeURIComponent(loginTenant)}` : '/login')
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  const continueAfterSignup = () => {
    if (pendingRedirectPath) {
      navigate(pendingRedirectPath, { replace: true })
    }
  }

  const copyBusinessCode = async () => {
    if (!businessCode) return
    try {
      await navigator.clipboard.writeText(businessCode)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Unable to copy. Please note the code down.')
    }
  }

  if (businessCode) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
        <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_22px_60px_rgba(15,23,42,0.12)]">
          <div className={cn('h-2 bg-gradient-to-r', config.brand.gradientClassName)} />
          <div className="space-y-6 p-8 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Check className="h-7 w-7" strokeWidth={3} />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900">You're all set</h1>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Save your business code. Every admin and team member joining {config.title.replace('Start your ', 'your ')} needs it to sign in.
              </p>
            </div>
            <div className="rounded-2xl border border-dashed border-primary/40 bg-primary/5 px-4 py-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary/70">Business code</p>
              <p className="mt-2 font-mono text-4xl font-black tracking-[0.15em] text-primary">{businessCode}</p>
              <Button type="button" variant="ghost" size="sm" className="mt-3 text-primary" onClick={copyBusinessCode}>
                {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
                {copied ? 'Copied' : 'Copy code'}
              </Button>
            </div>
            <Button type="button" size="lg" className="h-12 w-full rounded-xl text-base font-semibold" onClick={continueAfterSignup}>
              Continue
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <SignupSplitShell brand={config.brand} title={config.title} description={config.description}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8" noValidate>
        <FormSection step={1} title="About your business">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="company_name">{config.businessLabel}</Label>
              <IconInput id="company_name" icon={vertical === 'ecommerce' ? Store : Building2} placeholder={config.namePlaceholder} disabled={isLoading} {...register('company_name')} aria-invalid={!!errors.company_name} />
              <FieldError message={errors.company_name?.message} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="country_id">Country</Label>
              <input type="hidden" {...register('country_id')} />
              <Select value={selectedCountryId || ''} onValueChange={(value) => setValue('country_id', value, { shouldValidate: true, shouldDirty: true })} disabled={isLoading || countriesQuery.isLoading || !countriesQuery.data?.length}>
                <SelectTrigger id="country_id" aria-invalid={!!errors.country_id} className="!h-11 w-full rounded-xl bg-white">
                  <SelectValue placeholder={countriesQuery.isLoading ? 'Loading countries...' : 'Select a country'} />
                </SelectTrigger>
                <SelectContent>
                  {(countriesQuery.data ?? []).map((country) => (
                    <SelectItem key={String(country.id)} value={String(country.id)}>
                      {country.country_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {countriesQuery.isError && !errors.country_id ? <FieldError message="Unable to load countries right now." /> : null}
              <FieldError message={errors.country_id?.message} />
            </div>
          </div>
        </FormSection>

        <FormSection step={2} title="Your details">
          <div className="space-y-1.5">
            <Label htmlFor="owner_name">Full name</Label>
            <IconInput id="owner_name" icon={User} placeholder="Jane Doe" autoComplete="name" disabled={isLoading} {...register('owner_name')} aria-invalid={!!errors.owner_name} />
            <FieldError message={errors.owner_name?.message} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <IconInput id="email" icon={Mail} type="email" placeholder="name@example.com" autoComplete="email" disabled={isLoading} {...register('email')} aria-invalid={!!errors.email} />
              <FieldError message={errors.email?.message} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone_number">Phone number</Label>
              <PhoneInput id="phone_number" dialCode={selectedCountry?.dial_code} placeholder={PHONE_NUMBER_SAMPLE_PLACEHOLDER} disabled={isLoading} {...register('phone_number')} aria-invalid={!!errors.phone_number} />
              <FieldError message={errors.phone_number?.message} />
            </div>
          </div>
        </FormSection>

        <FormSection step={3} title="Secure your account">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" placeholder="Create a password" disabled={isLoading} {...register('password')} aria-invalid={!!errors.password} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password_confirm">Confirm password</Label>
              <PasswordInput id="password_confirm" placeholder="Repeat your password" disabled={isLoading} {...register('password_confirm')} aria-invalid={!!errors.password_confirm} />
              <FieldError message={errors.password_confirm?.message} />
            </div>
          </div>

          <PasswordChecklist rules={PASSWORD_RULES} value={password} showErrors={Boolean(errors.password)} />
        </FormSection>

        <div className="space-y-4">
          <SignupEmailCodeField emailCode={emailCode} email={watchedEmail} disabled={isLoading} />

          <AuthTermsDisclosure termsText={null} privacyText={null} />

          <Button type="submit" size="lg" className="h-12 w-full rounded-xl text-base font-semibold shadow-lg shadow-primary/20" disabled={isLoading || emailCode.sending}>
            {isLoading ? <Spinner className="mr-2" /> : null}
            {isLoading ? config.creatingLabel : emailCode.isAwaiting(watchedEmail) ? 'Verify and create account' : config.submitLabel}
            {isLoading ? null : <ArrowRight className="ml-1 h-4 w-4" />}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            Joining an existing organisation?{' '}
            <Link to="/signup" className="font-semibold text-primary hover:underline">
              Sign up as a team member
            </Link>
          </p>
        </div>
      </form>
    </SignupSplitShell>
  )
}
